import { DownOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Input, Popover, Tree } from 'antd';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import {
  buildDescendantLeafMap,
  collectLeafKeys,
  computeCheckedKeysForTree,
  extractLeafKeysFromChecked,
} from '@app/components/TreeCheckboxFilter/utils';
import { FilterMenuShell } from '@app/shared/components/FilterMenuShell';

import type { TreeDataNode, TreeProps } from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib';
import type { FC, Key } from 'react';

import '@app/components/TreeCheckboxFilter/styles/TreeCheckboxFilter.desktop.scss';
const PF = 'TreeCheckboxFilter';

const getNodeTitleText = (title: TreeDataNode['title']): string => {
  if (typeof title === 'string' || typeof title === 'number') {
    return String(title);
  }

  if (
    title &&
    typeof title === 'object' &&
    'props' in title &&
    title.props &&
    typeof title.props === 'object' &&
    'children' in title.props
  ) {
    const { children } = title.props;

    if (Array.isArray(children)) {
      return children.map(getNodeTitleText).join(' ');
    }

    return getNodeTitleText(children);
  }

  return '';
};

const filterTreeData = (
  nodes: TreeDataNode[],
  normalizedSearch: string
): TreeDataNode[] => {
  if (!normalizedSearch) return nodes;

  return nodes.reduce<TreeDataNode[]>((acc, node) => {
    const children = (node.children ?? []) as TreeDataNode[];
    const titleMatches = getNodeTitleText(node.title)
      .toLowerCase()
      .includes(normalizedSearch);
    const filteredChildren = filterTreeData(children, normalizedSearch);

    if (titleMatches || filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: titleMatches ? children : filteredChildren,
      });
    }

    return acc;
  }, []);
};

interface Props {
  title: string;
  disabled?: boolean;
  treeData: TreeDataNode[];
  value: Key[];
  onChange(nextLeafKeys: Key[], topLevelNode?: TreeDataNode): void;
  isMultipleSelect?: boolean;
  dataTestId?: string;
  popoverRootClassName?: string;
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
}

export const TreeCheckboxFilter: FC<Props> = ({
  title,
  disabled = false,
  treeData,
  value,
  onChange,
  isMultipleSelect = true,
  dataTestId = '',
  popoverRootClassName,
  getPopupContainer,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [userExpandedKeys, setUserExpandedKeys] = useState<Key[]>([]);
  const isActive = value.length > 0;
  const normalizedSearch = searchValue.trim().toLowerCase();

  const filteredTreeData = useMemo(
    () => filterTreeData(treeData, normalizedSearch),
    [treeData, normalizedSearch]
  );

  const descendantLeafMap = useMemo(
    () => buildDescendantLeafMap(treeData),
    [treeData]
  );
  const leafSet = useMemo(() => collectLeafKeys(treeData), [treeData]);
  const filteredDescendantLeafMap = useMemo(
    () => buildDescendantLeafMap(filteredTreeData),
    [filteredTreeData]
  );
  const filteredLeafSet = useMemo(
    () => collectLeafKeys(filteredTreeData),
    [filteredTreeData]
  );

  const selectedLeafSet = useMemo(() => new Set<Key>(value), [value]);
  const checkedKeysForTree = useMemo(
    () => computeCheckedKeysForTree(selectedLeafSet, descendantLeafMap),
    [selectedLeafSet, descendantLeafMap]
  );
  const hasSearchScope = normalizedSearch.length > 0;
  const scopeLeafSet = hasSearchScope ? filteredLeafSet : leafSet;
  const scopeLeafKeys = useMemo(() => Array.from(scopeLeafSet), [scopeLeafSet]);
  const checkAll = useMemo(
    () =>
      scopeLeafKeys.length > 0 &&
      scopeLeafKeys.every((key) => selectedLeafSet.has(key)),
    [scopeLeafKeys, selectedLeafSet]
  );
  const indeterminate = useMemo(() => {
    const hasChecked = scopeLeafKeys.some((key) => selectedLeafSet.has(key));

    return hasChecked && !checkAll;
  }, [checkAll, scopeLeafKeys, selectedLeafSet]);
  const expandedKeys = useMemo(
    () =>
      hasSearchScope
        ? Array.from(filteredDescendantLeafMap.keys()).filter(
            (key) => !filteredLeafSet.has(key)
          )
        : userExpandedKeys,
    [
      filteredDescendantLeafMap,
      filteredLeafSet,
      hasSearchScope,
      userExpandedKeys,
    ]
  );

  const findTopLevelNode = useCallback(
    (key: Key, nodes: TreeDataNode[]): TreeDataNode | null => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findTopLevelNode(key, node.children);
          if (found) return node;
        }
      }
      return null;
    },
    []
  );

  const handleCheck = useCallback<NonNullable<TreeProps['onCheck']>>(
    (keys, info) => {
      const activeDescendantLeafMap = hasSearchScope
        ? filteredDescendantLeafMap
        : descendantLeafMap;
      const activeLeafSet = hasSearchScope ? filteredLeafSet : leafSet;

      if (isMultipleSelect) {
        const arr = Array.isArray(keys)
          ? (keys as Key[])
          : (keys.checked as Key[]);
        const nextLeafKeys = extractLeafKeysFromChecked(
          arr,
          activeDescendantLeafMap,
          activeLeafSet
        );

        if (hasSearchScope) {
          const outsideScope = value.filter((key) => !activeLeafSet.has(key));

          onChange(Array.from(new Set([...outsideScope, ...nextLeafKeys])));
          return;
        }

        onChange(nextLeafKeys);
      } else {
        const clickedKey = info.node.key as Key;

        const topLevelNode =
          findTopLevelNode(clickedKey, treeData) ?? info.node;

        const collectKeys = (node: TreeDataNode, acc: Key[] = []) => {
          acc.push(node.key);
          node.children?.forEach((child) => collectKeys(child, acc));
          return acc;
        };

        const allowedKeys = collectKeys(topLevelNode);

        const filtered = Array.isArray(keys)
          ? (keys as Key[]).filter((k) => allowedKeys.includes(k))
          : (keys.checked as Key[]).filter((k) => allowedKeys.includes(k));

        const nextLeafKeys = extractLeafKeysFromChecked(
          filtered,
          descendantLeafMap,
          leafSet
        );

        onChange(nextLeafKeys, topLevelNode);
      }
    },
    [
      descendantLeafMap,
      filteredDescendantLeafMap,
      filteredLeafSet,
      findTopLevelNode,
      hasSearchScope,
      isMultipleSelect,
      leafSet,
      onChange,
      treeData,
      value,
    ]
  );

  const checkAllChangeHandler = useCallback(
    ({ target: { checked } }: CheckboxChangeEvent) => {
      if (hasSearchScope) {
        const outsideScope = value.filter((key) => !filteredLeafSet.has(key));
        const next = checked
          ? [...outsideScope, ...scopeLeafKeys]
          : outsideScope;

        onChange(Array.from(new Set(next)));
        return;
      }

      onChange(checked ? scopeLeafKeys : []);
    },
    [filteredLeafSet, hasSearchScope, onChange, scopeLeafKeys, value]
  );

  const expandHandler = useCallback<NonNullable<TreeProps['onExpand']>>(
    (nextExpandedKeys) => {
      setUserExpandedKeys(nextExpandedKeys);
    },
    []
  );

  const openChangeHandler = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setSearchValue('');
    }
  }, []);

  return (
    <Popover
      content={
        <FilterMenuShell
          className={`${PF}__popover`}
          title={title}
          meta={value.length > 0 ? `Выбрано: ${value.length}` : undefined}
        >
          <Input
            className={`${PF}__search full-width`}
            placeholder="Поиск"
            disabled={disabled}
            value={searchValue}
            onChange={({ target: { value } }) => setSearchValue(value)}
            data-testid="filter-search-input"
          />
          {scopeLeafKeys.length > 0 && (
            <>
              <Checkbox
                indeterminate={indeterminate}
                checked={checkAll}
                disabled={disabled}
                onChange={checkAllChangeHandler}
                data-testid="checkbox"
              >
                {hasSearchScope ? 'Выбрать найденное' : 'Выбрать все'}
              </Checkbox>
              <Divider className={`${PF}__divider`} />
            </>
          )}
          <Tree
            checkable
            selectable={false}
            blockNode
            treeData={filteredTreeData}
            disabled={disabled}
            checkedKeys={checkedKeysForTree}
            expandedKeys={expandedKeys}
            onExpand={expandHandler}
            onCheck={handleCheck}
            className={`${PF}__tree`}
          />
        </FilterMenuShell>
      }
      trigger="click"
      open={open && !disabled}
      onOpenChange={openChangeHandler}
      placement="bottomLeft"
      showArrow={false}
      arrow={false}
      rootClassName={cn(
        'FilterMenuShellPopover',
        `${PF}__popover-root`,
        popoverRootClassName
      )}
      classNames={{ body: `${PF}__popover-body` }}
      getPopupContainer={getPopupContainer}
    >
      <Button
        className={cn(`${PF}__button`, { [`${PF}__button--active`]: isActive })}
        size="small"
        htmlType="button"
        disabled={disabled}
        aria-expanded={open && !disabled}
        data-testid={dataTestId}
      >
        <span className={`${PF}__label`}>{title}</span>
        {value.length > 0 && (
          <span className={`${PF}__count`}>{value.length}</span>
        )}
        <DownOutlined
          className={cn(`${PF}__icon`, { [`${PF}__icon--opened`]: open })}
        />
      </Button>
    </Popover>
  );
};
